import React, {useState} from 'react';
import {Button, Box} from '@mui/material';
import {useMutation, gql} from '@apollo/client';
import {useSnackbar} from 'notistack';
import {useAutoForm} from '../../components/AutoForm/useAutoForm';
import withAutoFormNew from '../../components/AutoForm/AutoFormNew';
import AutoField, {
  ActionType,
  AutoTextField,
  SelectField,
} from '../../components/AutoForm/AutoFieldNew';
import {UPDATES} from '../../shared/UPDATES';

type PermissionDialogProps = object;

export type PermissionsFormValue = {
  accountId: string;
  contactId: string;
  staffId: string;
  searchTerm: string;
  delete: string;
  entityType: string;
};

type ContactOption = {
  value: string;
  label: string;
};

const DELETE_ACCOUNT = gql`
  mutation deleteAccount($accountId: UUID!) {
    deleteAccount(accountId: $accountId) {
      ok
      errors
    }
  }
`;

const DELETE_CONTACT = gql`
  mutation deleteContact($contactId: UUID!) {
    deleteContact(contactId: $contactId) {
      ok
      errors
    }
  }
`;

function ManageClientsPage() {
  const {enqueueSnackbar} = useSnackbar();
  const {values, setFieldValue} = useAutoForm<PermissionsFormValue>();
  const [contactOptions, setContactOptions] = useState<ContactOption[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<null | {
    type: 'account' | 'contact';
    id: string;
  }>(null);

  const [deleteAccount] = useMutation(DELETE_ACCOUNT, {
    onCompleted: (data) => {
      if (data.deleteAccount.ok) {
        enqueueSnackbar('Account deleted successfully', {variant: 'success'});
        setFieldValue(UPDATES._4162, 'accountId', '');
        setContactOptions([]);
      } else {
        enqueueSnackbar(`Failed to delete account: ${data.deleteAccount.errors}`, {
          variant: 'error',
        });
      }
    },
    onError: (err) => {
      enqueueSnackbar(`Unable to delete account. ${err.message}`, {
        variant: 'error',
        autoHideDuration: null,
      });
    },
  });

  const [deleteContact] = useMutation(DELETE_CONTACT, {
    onCompleted: (data) => {
      if (data.deleteContact.ok) {
        enqueueSnackbar('Contact deleted successfully', {variant: 'success'});
        setContactOptions((prev) =>
          prev.filter((contact) => contact.value !== deleteTarget?.id)
        );
      } else {
        enqueueSnackbar(`Failed to delete contact: ${data.deleteContact.errors}`, {
          variant: 'error',
        });
      }
    },
    onError: (err) => {
      enqueueSnackbar(`Unable to delete contact. ${err.message}`, {
        variant: 'error',
        autoHideDuration: null,
      });
    },
  });

  const handleSubmit = async () => {
    if (values.entityType === 'account') {
      const response = await deleteAccount({variables: {accountId: values.delete}});
      if (response.data.deleteAccount.ok) {
        enqueueSnackbar('Account deleted successfully', {variant: 'success'});
        setFieldValue(UPDATES._4163, 'delete', ''); // Clear the input field
      }
    } else if (values.entityType === 'contact') {
      const response = await deleteContact({variables: {contactId: values.delete}});
      if (response.data.deleteContact.ok) {
        enqueueSnackbar('Contact deleted successfully', {variant: 'success'});
        setFieldValue(UPDATES._4164, 'delete', ''); // Clear the input field
      }
    }
  };

  const clientSearchField: AutoTextField = {
    labelOnTop: true,
    id: 'delete',
    label: 'Delete',
    type: ActionType.text,
    initialValue: '',
    placeholder: 'Enter Account or Contact ID',
  };

  const entityTypeField: SelectField = {
    id: 'entityType',
    label: 'Entity Type',
    type: ActionType.select,
    options: [
      {
        value: 'account',
        label: 'Account',
      },
      {
        value: 'contact',
        label: 'Contact',
      },
    ],
  };

  return (
    <>
      <Box display="flex" p={2} bgcolor="white" borderRadius="3px">
        <Box flex={3}>
          <AutoField field={clientSearchField} />
        </Box>
        <Box flex={1} ml={3}>
          <AutoField field={entityTypeField} />
        </Box>
        <Box ml={3}>
          <Button variant="contained" color="primary" onClick={handleSubmit}>
            Submit
          </Button>
        </Box>
      </Box>
      <Box p={2} />
    </>
  );
}

const ManageClientsPageWrapped = withAutoFormNew<PermissionsFormValue, object>(
  ManageClientsPage,
  undefined,
  {debugPosition: 'bottom'}
);

export default ManageClientsPageWrapped;
